import React, { useEffect, useState, useContext } from "react";
import {
  Button,
  Spin,
  Row,
  Col,
  Divider,
  Descriptions,
  Radio,
  Empty,
  Tag,
} from "antd";
import {
  ArrowLeftOutlined,
  UserOutlined,
  CalendarOutlined,
  CheckCircleOutlined,
} from "@ant-design/icons";
import { Paper, TextField, Box, Typography } from "@mui/material";
import axios from "axios";
import { useParams, useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import toast from "react-hot-toast";
import { URL as BASE_URL } from "../config";
import { LoaderContext } from "../Components/dashboard";

const themeColor = "#4b0082";

export default function UserDetails() {
  const { setLoading } = useContext(LoaderContext);
  const { id } = useParams();
  const navigate = useNavigate();

  const [user, setUser] = useState(null);
  const [verificationFlag, setVerificationFlag] = useState(1);
  const [verificationFlags, setVerificationFlags] = useState([]);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [mobileNo, setMobileNo] = useState("");

  useEffect(() => {
    let isMounted = true;
    const startTime = Date.now();
    setLoading(true);

    const init = async () => {
      await Promise.all([fetchUser(), fetchVerificationFlags()]);
      const elapsed = Date.now() - startTime;
      const remaining = 1500 - elapsed;
      setTimeout(() => {
        if (isMounted) setLoading(false);
      }, remaining > 0 ? remaining : 0);
    };

    init();
    return () => { isMounted = false; };
  }, []);

  useEffect(() => {
    fetchUser();
    fetchVerificationFlags();
  }, [id]);

  const fetchUser = async () => {
    try {
      const res = await axios.get(`${BASE_URL}/userregistration/${id}`);
      const data = res.data;
      setUser(data);
      setVerificationFlag(data.fkVerificationFlagId || 1);
      setStartDate(data.packageStartDate ? dayjs(data.packageStartDate) : null);
      setEndDate(data.packageEnddate ? dayjs(data.packageEnddate) : null);
      setMobileNo(data.mobileNo != null ? String(data.mobileNo) : "");
    } catch {
      toast.error("Failed to load user");
    } finally {
      setLoading(false);
    }
  };

  const fetchVerificationFlags = async () => {
    try {
      const res = await axios.get(`${BASE_URL}/verificationflags`);
      setVerificationFlags(res.data);
    } catch {
      toast.error("Failed to load verification flags");
    }
  };

 const handleUpdate = async () => {
    const mobileStr = String(mobileNo || "");
  if (mobileStr.length !== 10) {
    return toast.error("कृपया वैध १० अंकी मोबाईल नंबर प्रविष्ट करा");
  }

    try {
      const formData = new FormData();
      formData.append("fkVerificationFlagId", verificationFlag);
      formData.append("mobileNo", mobileStr);

      if (startDate) formData.append("packageStartDate", startDate.format("YYYY-MM-DD"));
      if (endDate) formData.append("packageEnddate", endDate.format("YYYY-MM-DD"));

      await axios.patch(
        `${BASE_URL}/userregistration/${user.userId}`,
        formData
      );

      toast.success("User updated successfully");
      navigate(-1);
    } catch {
      toast.error("Update failed");
    }
  };

  if (!user) return <Empty description="User not found" />;

  const handleStartDateChange = (value) => {
    const start = dayjs(value);
    setStartDate(start);
    const autoEnd = start.add(1, "year");
    setEndDate(autoEnd);
  };

  return (
    <Box sx={{ p: { xs: 0.1, md: 1 } }}>
      <Paper
        elevation={0}
        sx={{
          mb: 3,
          borderRadius: 2,
          backgroundColor: "#fff",
        }}
      >
        <Row align="middle" justify="space-between" style={{ padding: "10px 16px" }}>
          <Col>
            <Box
              sx={{
                background: `linear-gradient(90deg, ${themeColor}, #b552fc)`,
                px: { xs: 2.5, md: 4 },
                py: { xs: 0.8, md: 1.1 },
                borderRadius: "8px 22px 8px 22px",
                boxShadow: "0 6px 16px rgba(255,92,110,0.35)",
              }}
            >
              <Typography
                sx={{
                  fontSize: "18px",
                  fontWeight: 900,
                  color: "#fff",
                  letterSpacing: "0.7px",
                  textTransform: "uppercase",
                  lineHeight: 1.2,
                }}
              >
                User Details
              </Typography>
            </Box>
          </Col>
          <Col>
            <Button
              icon={<ArrowLeftOutlined />}
              onClick={() => navigate(-1)}
              style={{
                borderRadius: 20,
                border: `1px solid ${themeColor}`,
                color: themeColor,
                fontWeight: 600,
                padding: "4px 16px",
              }}
            >
              Back
            </Button>
          </Col>
        </Row>
      </Paper>

      <Paper elevation={3} sx={{ p: 3, borderRadius: 3, mb: 3 }}>
        <Typography
          variant="subtitle1"
          fontWeight={700}
          sx={{ mb: 2, color: themeColor }}
        >
          Basic Information
        </Typography>

        <Descriptions
          bordered
          column={{ xs: 1, sm: 2, md: 2 }}
          size="middle"
          labelStyle={{ fontWeight: 700 }}
        >
          <Descriptions.Item label="Name">{user.userName}</Descriptions.Item>
          <Descriptions.Item label="Mobile">
            <TextField
              size="small"
              variant="standard"
              value={mobileNo}
              // फक्त अंक टाईप व्हावेत यासाठी लहान लॉजिक
              onChange={(e) => setMobileNo(e.target.value.replace(/\D/g, "").slice(0, 10))}
              sx={{ input: { fontWeight: 600, color: themeColor } }}
            />
          </Descriptions.Item>

          <Descriptions.Item label="Email">{user.email || "-"}</Descriptions.Item>
          <Descriptions.Item label="Password">{user.password || "-"}</Descriptions.Item>
          <Descriptions.Item label="Birthdate">
            {user.birthdate ? dayjs(user.birthdate).format("DD-MM-YYYY") : "-"}
          </Descriptions.Item>
          <Descriptions.Item label="Device">{user.deviceName || "-"}</Descriptions.Item>
          <Descriptions.Item label="Latitude">{user.latitude ?? "-"}</Descriptions.Item>
          <Descriptions.Item label="Longitude">{user.longitude ?? "-"}</Descriptions.Item>
          <Descriptions.Item label="Address" span={2}>
            {user.address || "-"}
          </Descriptions.Item>
          <Descriptions.Item label="Note" span={2}>
            {user.note || "-"}
          </Descriptions.Item>
        </Descriptions>
      </Paper>

      <Paper elevation={3} sx={{ p: 3, borderRadius: 3 }}>
        <Typography
          variant="subtitle1"
          fontWeight={700}
          sx={{ mb: 2, color: themeColor }}
        >
          Verification & Package
        </Typography>

        <Row gutter={[24, 24]}>
          <Col xs={24} md={10}>
            <Typography fontWeight={600} mb={1}>
              <CheckCircleOutlined /> Verification Status
            </Typography>

            <Radio.Group
              value={verificationFlag}
              onChange={(e) => setVerificationFlag(e.target.value)}
            >
              <Row gutter={[8, 8]}>
                {verificationFlags.map((flag) => (
                  <Col key={flag.verificationFlagId}>
                    <Radio value={flag.verificationFlagId}>
                      <Tag color={themeColor}>{flag.verificationFlagName}</Tag>
                    </Radio>
                  </Col>
                ))}
              </Row>
            </Radio.Group>
          </Col>
          <Col xs={24} md={7}>
            <TextField
              fullWidth
              type="date"
              size="small"
              label="Package Start"
              value={startDate ? startDate.format("YYYY-MM-DD") : ""}
              onChange={(e) => handleStartDateChange(e.target.value)}
              InputLabelProps={{
                shrink: true,
                sx: { color: "#4b0082", "&.Mui-focused": { color: "#4b0082" } },
              }}
              sx={{
                mb: { xs: 2, md: 0 },
                "& .MuiOutlinedInput-root": {
                  "& fieldset": { borderColor: "#9a9a9a" },
                  "&:hover fieldset": { borderColor: "#000" },
                  "&.Mui-focused fieldset": { borderColor: "#000" },
                },
              }}
            />
          </Col>
          <Col xs={24} md={7}>
            <TextField
              fullWidth
              type="date"
              size="small"
              label="Package End"
              value={endDate ? endDate.format("YYYY-MM-DD") : ""}
              onChange={(e) => setEndDate(dayjs(e.target.value))}
              InputLabelProps={{
                shrink: true,
                sx: { color: "#4b0082", "&.Mui-focused": { color: "#4b0082" } },
              }}
              inputProps={{
                min: startDate ? startDate.format("YYYY-MM-DD") : undefined,
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  "& fieldset": { borderColor: "#9a9a9a" },
                  "&:hover fieldset": { borderColor: "#000" },
                  "&.Mui-focused fieldset": { borderColor: "#000" },
                },
              }}
            />
          </Col>
        </Row>

        <Divider sx={{ my: 3 }} />

        <Row justify="end">
          <Button
            type="primary"
            size="large"
            icon={<CalendarOutlined />}
            style={{
              background: themeColor,
              borderRadius: 30,
              padding: "0 42px",
            }}
            onClick={handleUpdate}
          >
            Submit
          </Button>
        </Row>
      </Paper>
    </Box>
  );
}